import {normalizeAccessContext} from '@/utils/access-context-normalization';

function normalizeObject(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

export function normalizeRouteMeta(meta = {}) {
    const source = normalizeObject(meta);
    const middleware = Array.isArray(source.middleware)
        ? source.middleware
        : (source.middleware ? [source.middleware] : []);

    return {
        middleware,
        requires_registration: middleware.includes('register'),
        requires_guest: middleware.includes('guest'),
        requires_auth: middleware.includes('auth'),
        requires_workspace: !!source.requires_workspace || !!source.requires_dashboard || !!source.workspace_access || !!source.dashboard_access,
        requires_admin: !!source.requires_admin || !!source.admin_only,
        requires_customer_scope: !!source.requires_customer_scope || !!source.customer_only,
        required_capability: source.required_capability || source.required_controller || source.controller || null,
    };
}

export function canAccessCustomerRoute(path, access) {
    const normalizedPath = String(path || '');
    const modules = access && access.modules ? access.modules : {};

    if (normalizedPath === '/dashboard') {
        return !!access && !!access.is_customer;
    }
    if (normalizedPath.startsWith('/tickets')) {
        return !!access && !!access.is_customer;
    }
    if (normalizedPath.startsWith('/timeline') || normalizedPath.startsWith('/positions/timeline')) {
        return !!modules.timeline;
    }
    if (normalizedPath.startsWith('/positions')) {
        return !!modules.positions;
    }
    if (normalizedPath.startsWith('/projects')) {
        return !!access && !!access.is_customer && !!modules.projects;
    }
    if (normalizedPath.startsWith('/relationships')) {
        return !!access && !!access.is_customer && !!modules.relationships;
    }
    if (normalizedPath.startsWith('/boards')) {
        return !!access && !!access.is_customer && !!modules.boards;
    }
    if (normalizedPath.startsWith('/file-manager')) {
        return !!modules.file_manager && !!access && !!access.is_customer;
    }
    if (normalizedPath.startsWith('/documentations')) {
        return !!modules.customer_documentation
            && !!access
            && (!!access.is_customer || !!access.is_guest);
    }
    if (normalizedPath.startsWith('/finance')) {
        return !!modules.finance && !!access && !!access.is_customer;
    }
    if (normalizedPath.startsWith('/vault')) {
        return !!modules.vault && !!access && !!access.is_customer;
    }
    if (normalizedPath.startsWith('/insights')) {
        return !!modules.insights && !!access && !!access.is_customer;
    }
    if (normalizedPath.startsWith('/system-extensions')) {
        return !!access
            && !!access.is_customer
            && (!!modules.cms || !!modules.insights);
    }

    return true;
}

function canAccessCapabilityRoute(path, meta, access) {
    if (!meta || !meta.required_capability || !access || !access.workspace_access) {
        return true;
    }

    if (
        path.startsWith('/workspace/relationships/companies/')
        && meta.requires_admin
    ) {
        return !!(access.capabilities && access.capabilities[meta.required_capability]);
    }
    if (
        (
            path.startsWith('/workspace/admin/users')
            || path.startsWith('/workspace/relationships/users')
        )
        && meta.requires_admin
    ) {
        return !!(access.capabilities && access.capabilities[meta.required_capability]);
    }

    if (path.startsWith('/workspace/projects')) {
        return !!access.modules.projects;
    }
    if (path.startsWith('/workspace/relationships')) {
        return !!access.modules.relationships;
    }
    if (path.startsWith('/workspace/vault')) {
        return !!access.modules.vault;
    }
    if (path.startsWith('/workspace/insights')) {
        return !!access.modules.insights;
    }
    if (path.startsWith('/workspace/finance')) {
        return !!access.modules.finance;
    }
    if (path.startsWith('/workspace/file-manager')) {
        return !!access.modules.file_manager;
    }
    if (path.startsWith('/workspace/documentation')) {
        return !!access.modules.documentation;
    }
    if (path.startsWith('/workspace/boards')) {
        return !!access.modules.boards;
    }
    if (path.startsWith('/workspace/cms')) {
        return !!access.modules.cms;
    }
    if (path.startsWith('/workspace/timeline')) {
        return !!access.modules.timeline;
    }
    if (path.startsWith('/workspace/positions')) {
        return !!access.modules.positions;
    }
    if (path.startsWith('/workspace/tickets')) {
        return !!access.modules.tickets;
    }

    return !!(access.capabilities && access.capabilities[meta.required_capability]);
}

export function canAccessRoute({path = '', meta = {}, access = null, hasToken = true} = {}) {
    const currentAccess = normalizeAccessContext(access);
    const normalizedMeta = normalizeRouteMeta(meta);

    if (normalizedMeta.requires_guest) {
        return !hasToken;
    }
    if (normalizedMeta.requires_auth && !hasToken) {
        return false;
    }
    if (normalizedMeta.requires_workspace && !currentAccess.workspace_access) {
        return false;
    }
    if (normalizedMeta.requires_admin && !currentAccess.is_admin) {
        return false;
    }
    if (normalizedMeta.requires_customer_scope && !canAccessCustomerRoute(path, currentAccess)) {
        return false;
    }
    if (!canAccessCapabilityRoute(path, normalizedMeta, currentAccess)) {
        return false;
    }
    if (path.startsWith('/workspace/insights') && !currentAccess.is_admin && !currentAccess.modules.insights) {
        return false;
    }
    if (
        path.startsWith('/workspace/system-extensions')
        && !currentAccess.modules.cms
        && !currentAccess.modules.insights
    ) {
        return false;
    }
    if (currentAccess.is_customer && path.startsWith('/workspace/vault') && !currentAccess.modules.vault) {
        return false;
    }

    return true;
}
